import { EmailPreviewSheet } from '@/components/email/EmailPreviewSheet';
import { SanctionBadge } from '@/components/partners/SanctionBadge';
import { SegmentBadge } from '@/components/partners/SegmentBadge';
import { Button, Card, CardContent, Input } from '@/components/ui';
import { useBulkSelection } from '@/hooks/use-bulk-selection';
import { usePartners, usePartnerStats } from '@/hooks/use-partners';
import { useTranslation } from '@kgc/i18n';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PARTNER_CATEGORIES, PARTNER_STATUSES, PARTNER_TYPES } from './mock-data';
import type { PartnerCategory, PartnerStatus, PartnerType } from './types';

export function PartnerListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<PartnerType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<PartnerStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<PartnerCategory | 'all'>('all');
  const [showEmailSheet, setShowEmailSheet] = useState(false);

  const {
    selectedIds,
    selectedCount,
    toggleSelect,
    toggleSelectAll,
    isSelected,
    isAllSelected,
    clearSelection,
  } = useBulkSelection();

  // Build API filters from UI filters
  const apiFilters = useMemo(() => {
    const filters: {
      type?: 'INDIVIDUAL' | 'COMPANY';
      status?: 'ACTIVE' | 'INACTIVE' | 'BLACKLISTED' | 'TRUSTED' | 'WARNING';
      search?: string;
      pageSize?: number;
      // Interim ceiling (BE-max=100, Javo-ratify): a client-side category filter + select-all
      // a teljes halmazon kell működjön → load-all-at-100 (data-loss 20→100). Full server-mode
      // a BE category-filter-support után (ADR-followup).
    } = { pageSize: 100 };
    if (typeFilter !== 'all') {
      filters.type = typeFilter === 'individual' ? 'INDIVIDUAL' : 'COMPANY';
    }
    if (statusFilter !== 'all') {
      const statusMap: Record<
        string,
        'ACTIVE' | 'INACTIVE' | 'BLACKLISTED' | 'TRUSTED' | 'WARNING'
      > = {
        active: 'ACTIVE',
        inactive: 'INACTIVE',
        blocked: 'BLACKLISTED',
        trusted: 'TRUSTED',
        warning: 'WARNING',
      };
      filters.status = statusMap[statusFilter];
    }
    if (searchTerm) {
      filters.search = searchTerm;
    }
    return filters;
  }, [typeFilter, statusFilter, searchTerm]);

  const { partners, isLoading, error } = usePartners(apiFilters);
  const { stats } = usePartnerStats();

  // Client-side category filter (since API doesn't support it)
  const filteredPartners = useMemo(() => {
    if (categoryFilter === 'all') return partners;
    return partners.filter(partner => partner.categories.includes(categoryFilter));
  }, [partners, categoryFilter]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getStatusBadge = (status: PartnerStatus) => {
    const config = PARTNER_STATUSES.find(s => s.value === status);
    return (
      <span className={`rounded-full px-2 py-1 text-xs font-medium ${config?.color ?? ''}`}>
        {config ? t(config.labelKey) : status}
      </span>
    );
  };

  const getCategoryBadges = (categories: PartnerCategory[]) => {
    return categories.map(cat => {
      const config = PARTNER_CATEGORIES.find(c => c.value === cat);
      return (
        <span
          key={cat}
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${config?.color ?? ''}`}
        >
          {config ? t(config.labelKey) : cat}
        </span>
      );
    });
  };

  // Stats from API (fallback to computed if not loaded)
  const displayStats = stats ?? {
    total: partners.length,
    active: partners.filter(p => p.status === 'active').length,
    companies: partners.filter(p => p.type === 'company').length,
    withBalance: partners.filter(p => p.stats.outstandingBalance > 0).length,
    totalBalance: partners.reduce((sum, p) => sum + p.stats.outstandingBalance, 0),
  };

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="shadow-sm kgc-card-bg">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {t('partner.title')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('partner.subtitle')}</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/partners/new')}
            className="bg-primary hover:bg-primary/90"
          >
            {t('partner.newPartner')}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Error state */}
        {error && (
          <Card className="mb-6 border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
            <CardContent className="pt-4">
              <p className="text-red-800 dark:text-red-200">
                {t('page.errorPrefix')}: {error}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Stats cards */}
        <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-5">
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {displayStats.total}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('partner.totalPartners')}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {displayStats.active}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('status.active')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {displayStats.companies}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('partner.company')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {displayStats.withBalance}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('partner.withBalance')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                {formatPrice(displayStats.totalBalance)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('partner.totalBalance')}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="min-w-[250px] flex-1">
                <Input
                  placeholder={t('partner.searchPlaceholder')}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value as PartnerType | 'all')}
                className="rounded-md border px-3 py-2 border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
              >
                <option value="all">{t('page.allTypes')}</option>
                {PARTNER_TYPES.map(tp => (
                  <option key={tp.value} value={tp.value}>
                    {t(tp.labelKey)}
                  </option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as PartnerStatus | 'all')}
                className="rounded-md border px-3 py-2 border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
              >
                <option value="all">{t('page.allStatuses')}</option>
                {PARTNER_STATUSES.map(s => (
                  <option key={s.value} value={s.value}>
                    {t(s.labelKey)}
                  </option>
                ))}
              </select>
              <select
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value as PartnerCategory | 'all')}
                className="rounded-md border px-3 py-2 border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
              >
                <option value="all">{t('page.allCategories')}</option>
                {PARTNER_CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>
                    {t(c.labelKey)}
                  </option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions Bar */}
        {selectedCount > 0 && (
          <div className="mb-4 flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-800 dark:bg-blue-900/20">
            <span className="font-medium text-blue-800 dark:text-blue-200">
              {selectedCount} {t('email.bulkSend.partnersSelected')}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={clearSelection}>
                {t('common.cancel')}
              </Button>
              <Button size="sm" onClick={() => setShowEmailSheet(true)}>
                {t('email.bulkSend.sendEmail')}
              </Button>
            </div>
          </div>
        )}

        {/* Partners list */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="py-12 text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
                <p className="mt-2 text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b bg-gray-50 dark:bg-slate-700/50">
                    <tr>
                      <th className="w-10 px-2 py-3">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 dark:border-slate-500"
                          checked={isAllSelected(filteredPartners.map(p => p.id))}
                          onChange={() => toggleSelectAll(filteredPartners.map(p => p.id))}
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('table.partner')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('common.type')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('partner.categories')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('partner.contact')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('partner.revenue')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('partner.balance')}
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('table.status')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y dark:divide-slate-700">
                    {filteredPartners.map(partner => (
                      <tr
                        key={partner.id}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50"
                        onClick={() => navigate(`/partners/${partner.id}`)}
                      >
                        <td className="w-10 px-2 py-3" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 dark:border-slate-500"
                            checked={isSelected(partner.id)}
                            onChange={() => toggleSelect(partner.id)}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-900 dark:text-gray-100">
                                {partner.name}
                              </p>
                              {partner.segment && (
                                <SegmentBadge segment={partner.segment} compact />
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {partner.code}
                              </p>
                              <SanctionBadge
                                transferBanLevel={partner.transferBanLevel ?? 'NONE'}
                                transferBanUntil={partner.transferBanUntil}
                                bookingRestricted={partner.bookingRestricted}
                                bookingRestrictedUntil={partner.bookingRestrictedUntil}
                                bookingWarningFlag={partner.bookingWarningFlag}
                                compact
                              />
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600 dark:text-gray-300">
                            {partner.type === 'company'
                              ? t('partner.companyLabel')
                              : t('partner.individualLabel')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {getCategoryBadges(partner.categories)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            {partner.email && (
                              <p className="text-gray-600 dark:text-gray-300">{partner.email}</p>
                            )}
                            {partner.phone && (
                              <p className="text-gray-500 dark:text-gray-400">{partner.phone}</p>
                            )}
                            {/* M1 client feedback: type-specific info on partner cards */}
                            {partner.type === 'company' && partner.taxNumber && (
                              <p className="text-gray-400 dark:text-gray-500 text-xs">
                                {t('partner.taxNumber')}: {partner.taxNumber}
                              </p>
                            )}
                            {partner.type === 'individual' && partner.birthDate && (
                              <p className="text-gray-400 dark:text-gray-500 text-xs">
                                {t('partner.birthDate')}:{' '}
                                {new Date(partner.birthDate).toLocaleDateString('hu-HU')}
                              </p>
                            )}
                            {partner.city && (
                              <p className="text-gray-400 dark:text-gray-500 text-xs">
                                {partner.postalCode ? `${partner.postalCode} ` : ''}
                                {partner.city}
                                {partner.address ? `, ${partner.address}` : ''}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {formatPrice(partner.stats.totalRevenue)}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {partner.stats.totalOrders} {t('partner.ordersCount')}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {partner.stats.outstandingBalance > 0 ? (
                            <p className="font-medium text-red-600 dark:text-red-400">
                              {formatPrice(partner.stats.outstandingBalance)}
                            </p>
                          ) : (
                            <p className="text-gray-400 dark:text-gray-300">-</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">{getStatusBadge(partner.status)}</td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={e => {
                              e.stopPropagation();
                              navigate(`/partners/${partner.id}`);
                            }}
                          >
                            {t('common.details')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredPartners.length === 0 && !isLoading && (
                  <div className="py-12 text-center text-gray-500 dark:text-gray-400">
                    <p>{t('page.noFilterResults')}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          {/* A 'partner.displayedCount' = "Megjelenített: {count}" (interpoláció) → a count-ot
              a t()-be kell adni, NEM külön prefix-elni (eddig "3 Megjelenített: {count}"-ot rendert,
              kitöltetlen placeholderrel). Single-brace KGC-i18n. */}
          {t('partner.displayedCount', { count: filteredPartners.length })}
        </p>
      </main>

      <EmailPreviewSheet
        open={showEmailSheet}
        onOpenChange={setShowEmailSheet}
        recipients={filteredPartners
          .filter(p => selectedIds.has(p.id))
          .map(p => ({ id: p.id, name: p.name, email: p.email }))}
        onSendComplete={clearSelection}
      />
    </div>
  );
}
